<template>
  <div class="p-0">
    <router-link to="/leave/admin/articles" class="inline-flex items-center text-sm font-semibold text-gray-600 hover:text-gray-900 mb-5">
      <i class="fa-solid fa-arrow-left mr-2"></i>
      Volver a articulos
    </router-link>

    <header class="mb-5">
      <p class="breadcrumb">Licencias y permisos / Administracion / Articulos</p>
      <h1 class="text-2xl font-bold text-gray-900">{{ isEdit ? 'Editar articulo' : 'Nuevo articulo' }}</h1>
    </header>

    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <form v-else class="grid grid-cols-1 xl:grid-cols-3 gap-5" @submit.prevent="save">
      <section class="panel xl:col-span-2">
        <div class="panel-heading">
          <i class="fa-solid fa-circle-info text-indigo-800"></i>
          <h2 class="font-bold text-gray-900">Identificacion</h2>
        </div>

        <div v-if="error" class="alert-error">{{ error }}</div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2">
            <label class="label">Documento legal *</label>
            <select v-model="form.legal_document_id" required class="field">
              <option value="">Seleccionar documento</option>
              <option v-for="document in legalDocuments" :key="documentId(document)" :value="documentId(document)">
                {{ document.title }} ({{ document.code }})
              </option>
            </select>
          </div>
          <div>
            <label class="label">Codigo *</label>
            <input v-model.trim="form.code" required class="field" placeholder="ART_74" />
          </div>
          <div>
            <label class="label">Numero de articulo *</label>
            <input v-model.trim="form.article_number" required class="field" placeholder="74" />
          </div>
          <div class="md:col-span-2">
            <label class="label">Nombre *</label>
            <input v-model.trim="form.name" required class="field" placeholder="Razones particulares" />
          </div>
          <div class="md:col-span-2">
            <label class="label">Categoria *</label>
            <select v-model="form.category" required class="field">
              <option value="">Seleccionar categoria</option>
              <option v-for="category in categories" :key="category.value" :value="category.value">
                {{ category.label }}
              </option>
            </select>
          </div>
          <div class="md:col-span-2">
            <label class="toggle-row border-0 pb-0">
              <span>
                <strong>Permite varios dias</strong>
                <small>Si se activa, el articulo permite solicitudes de varios dias.</small>
              </span>
              <input v-model="form.allows_multi_day" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
            </label>
          </div>
          <div class="md:col-span-2">
            <label class="toggle-row border-0 pb-0">
              <span>
                <strong>Requiere documento final</strong>
                <small>Si se activa, el articulo exige presentar una constancia o documento final.</small>
              </span>
              <input v-model="form.requires_final_document" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
            </label>
          </div>
        </div>
      </section>

      <aside class="panel">
        <div class="panel-heading">
          <i class="fa-solid fa-sliders text-indigo-800"></i>
          <h2 class="font-bold text-gray-900">Comportamiento</h2>
        </div>

        <div class="space-y-4">
          <label class="toggle-row">
            <span>
              <strong>Pagado</strong>
              <small>Campo documentado: paid</small>
            </span>
            <input v-model="form.paid" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
          </label>
          <label class="toggle-row">
            <span>
              <strong>Requiere documentacion</strong>
              <small>Campo documentado: requires_documentation</small>
            </span>
            <input v-model="form.requires_documentation" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
          </label>
          <label class="toggle-row">
            <span>
              <strong>Puede superponerse</strong>
              <small>Campo documentado: can_overlap</small>
            </span>
            <input v-model="form.can_overlap" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
          </label>
          <label class="toggle-row">
            <span>
              <strong>Activo</strong>
              <small>Campo documentado: active</small>
            </span>
            <input v-model="form.active" type="checkbox" class="rounded border-gray-300 text-indigo-700" />
          </label>
        </div>

        <div class="mt-8 flex flex-col gap-3">
          <button type="submit" :disabled="saving" class="primary-button disabled:opacity-50">
            {{ saving ? 'Guardando...' : 'Guardar articulo' }}
          </button>
          <router-link to="/leave/admin/articles" class="secondary-button">Cancelar</router-link>
        </div>
      </aside>
    </form>

    <aside v-if="!isEdit" class="mt-5 info-box">
      <i class="fa-solid fa-circle-info text-indigo-700 mt-1"></i>
      <div>
        <h3 class="text-sm font-bold text-gray-900">Tras crear</h3>
        <p class="mt-1 text-sm text-gray-600">Despues de guardar, podes configurar reglas y requisitos desde el detalle del articulo.</p>
      </div>
    </aside>
  </div>
</template>

<script>
import Loading from '@/components/commons/Loading.vue';
import { legalDocumentsApi, leaveArticlesApi } from '@/service/leave.service';
import { LEAVE_CATEGORIES, unwrapData, unwrapList } from '@/utils/leave';

const emptyForm = () => ({
  legal_document_id: '',
  code: '',
  article_number: '',
  name: '',
  category: '',
  paid: false,
  requires_documentation: false,
  can_overlap: false,
  allows_multi_day: false,
  requires_final_document: false,
  active: true,
});

export default {
  name: 'LeaveAdminArticleFormView',
  components: { Loading },
  data() {
    return {
      form: emptyForm(),
      legalDocuments: [],
      loading: false,
      saving: false,
      error: '',
    };
  },
  computed: {
    isEdit() {
      return Boolean(this.$route.params.id);
    },
    categories() {
      return LEAVE_CATEGORIES.filter((category) => category.value);
    },
  },
  async mounted() {
    await this.fetchLegalDocuments();
    if (this.isEdit) {
      await this.fetchArticle();
    }
  },
  methods: {
    async fetchLegalDocuments() {
      try {
        const response = await legalDocumentsApi.list({ active: true });
        this.legalDocuments = unwrapList(response);
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudieron cargar los documentos legales.';
      }
    },
    async fetchArticle() {
      this.loading = true;
      this.error = '';
      try {
        const response = await leaveArticlesApi.get(this.$route.params.id);
        const article = unwrapData(response) || {};
        this.form = { ...emptyForm(), ...article };
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo cargar el articulo.';
      } finally {
        this.loading = false;
      }
    },
    documentId(document) {
      return document.id || document.document_id;
    },
    payload() {
      return {
        legal_document_id: this.form.legal_document_id,
        code: this.form.code,
        article_number: this.form.article_number,
        name: this.form.name,
        category: this.form.category,
        paid: Boolean(this.form.paid),
        requires_documentation: Boolean(this.form.requires_documentation),
        can_overlap: Boolean(this.form.can_overlap),
        allows_multi_day: Boolean(this.form.allows_multi_day),
        requires_final_document: Boolean(this.form.requires_final_document),
        active: Boolean(this.form.active),
      };
    },
    responseId(response) {
      const article = unwrapData(response) || {};
      return article.id || article.article_id || article.leave_article_id;
    },
    async save() {
      this.saving = true;
      this.error = '';
      try {
        if (this.isEdit) {
          await leaveArticlesApi.update(this.$route.params.id, this.payload());
          this.$router.push(`/leave/admin/articles/${this.$route.params.id}`);
          return;
        }
        const response = await leaveArticlesApi.create(this.payload());
        const id = this.responseId(response);
        this.$router.push(id ? `/leave/admin/articles/${id}` : '/leave/admin/articles');
      } catch (error) {
        this.error = error?.response?.data?.message || 'No se pudo guardar el articulo.';
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.breadcrumb {
  @apply text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2;
}
.panel {
  @apply bg-white border border-gray-200 rounded-xl shadow-sm p-5;
}
.panel-heading {
  @apply flex items-center gap-3 border-b border-gray-100 pb-4 mb-5;
}
.field {
  @apply w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600;
}
.label {
  @apply block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1;
}
.toggle-row {
  @apply flex items-start justify-between gap-4 border-b border-gray-100 pb-4 last:border-0;
}
.toggle-row strong {
  @apply block text-sm font-semibold text-gray-900;
}
.toggle-row small {
  @apply block text-xs text-gray-500;
}
.primary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold bg-indigo-700 text-white rounded-md hover:bg-indigo-800 shadow-sm;
}
.secondary-button {
  @apply inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50;
}
.alert-error {
  @apply bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4;
}
.info-box {
  @apply bg-indigo-50 border-l-4 border-indigo-500 rounded-r-lg p-4 flex gap-3;
}
</style>
